import { motion } from "framer-motion";
import {
  UserPlus,
  FileText,
  Package,
  Scale,
  Wallet,
  Warehouse,
  Factory,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function CollectionProcess() {
  const steps = [
    {
      number: 1,
      icon: UserPlus,
      title: "Enrollment with Contract",
      subtitle: "Digital Onboarding",
      description:
        "FBO registration with auto-generated digital contract and self-declaration",
      enabled: "B-Trace App registration + contract generation",
      color: "from-blue-500 to-blue-600",
      image:
        "https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb250cmFjdCUyMHNpZ25pbmclMjBidXNpbmVzc3xlbnwxfHx8fDE3NjUwODk3NDJ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    {
      number: 2,
      icon: Package,
      title: "Storage Can Distribution",
      subtitle: "Infrastructure Setup",
      description: "Standardized storage containers delivered to FBO premises",
      enabled: "Physical drums/cans dispatched post-enrollment",
      color: "from-indigo-500 to-indigo-600",
      image:
        "https://images.unsplash.com/photo-1687724733595-566fe8681ba0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdG9yYWdlJTIwY29udGFpbmVycyUyMGRydW1zfGVufDF8fHx8MTc2NTA4OTc0M3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    {
      number: 3,
      icon: Scale,
      title: "Oil Collection with Weightment",
      subtitle: "IoT-Verified Collection",
      description:
        "Pickup request initiated by FBO, oil collected and digitally weighed",
      enabled: "B-Trace pickup request + IoT weight logging",
      color: "from-purple-500 to-purple-600",
      image:
        "https://images.unsplash.com/photo-1729909019771-7728f56647f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1c2VkJTIwY29va2luZyUyMG9pbCUyMHJlc3RhdXJhbnR8ZW58MXx8fHwxNzY1MDg5NzQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    {
      number: 4,
      icon: Wallet,
      title: "Purchase Receipt & Instant Payment",
      subtitle: "Immediate Settlement",
      description:
        "Auto-generated receipt and instant payment to vendor account",
      enabled: "System-generated receipt + instant payment processing",
      color: "from-violet-500 to-violet-600",
      image:
        "https://images.unsplash.com/photo-1607609972246-a14762f20d3e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwcGF5bWVudCUyMGluc3RhbnR8ZW58MXx8fHwxNzY1MDg5NzQ0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    {
      number: 5,
      icon: Warehouse,
      title: "To Warehouse",
      subtitle: "Consolidation & Storage",
      description:
        "Collected feedstock transported and stored at central facility",
      enabled: "GPS-tracked logistics + warehouse entry logging",
      color: "from-fuchsia-500 to-fuchsia-600",
      image:
        "https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MXx8fHwxNzY1MDgxMDAyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    {
      number: 6,
      icon: Factory,
      title: "To Biofuels Factory",
      subtitle: "Final Delivery",
      description:
        "Quality-verified feedstock dispatched to biofuel manufacturers",
      enabled: "Batch traceability + ISCC documentation",
      color: "from-pink-500 to-pink-600",
      image:
        "https://images.unsplash.com/photo-1725802867239-ab1379895088?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaW9mdWVsJTIwZmFjdG9yeXxlbnwxfHx8fDE3NjUwODk3NDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
  ];

  const benefits = [
    { icon: CheckCircle2, text: "100% batch-level traceability" },
    { icon: CheckCircle2, text: "IoT-enabled, timestamped data capture" },
    { icon: CheckCircle2, text: "Digital contracts without manual paperwork" },
    {
      icon: CheckCircle2,
      text: "Immediate payment disbursement for vendor trust",
    },
  ];

  return (
    <section
      id="process"
      className="py-20 sm:py-32 bg-gradient-to-br from-white via-blue-50/30 to-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%237CB342' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border border-[#7CB342]/20 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-4 sm:mb-6"
          >
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-[#7CB342]" />
            <span className="text-gray-900 font-semibold text-xs sm:text-sm">
              Collection Workflow
            </span>
          </motion.div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            6-Step Digital
            <span className="block mt-2 bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
              Collection Process
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            End-to-end digital workflow ensuring complete traceability and
            vendor satisfaction
          </p>
        </motion.div>

        {/* Process Steps - Mobile Optimized */}
        <div className="relative mb-12 sm:mb-16">
          {/* Desktop Connection Line */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-orange-200"></div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 relative">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                {/* Card */}
                <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 relative z-10 h-full">
                  {/* Step Number Badge */}
                  <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4">
                    <div className={`bg-gradient-to-br ${step.color} w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white shadow-lg font-bold text-sm sm:text-base`}>
                      {step.number}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className={`bg-gradient-to-br ${step.color} w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>

                  {/* Content */}
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2 leading-tight">
                    {step.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 font-medium">
                    {step.subtitle}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Enabled By */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 rounded-lg sm:rounded-xl p-2.5 sm:p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <span className="text-[#1976D2] font-bold">✓</span>{" "}
                      {step.enabled}
                    </p>
                  </div>

                  {/* Image */}
                  <div className="mt-4">
                    <ImageWithFallback
                      src={step.image}
                      alt={step.title}
                      className="w-full h-40 sm:h-52 object-cover rounded-lg sm:rounded-xl"
                    />
                  </div>
                </div>

                {/* Mobile Arrow Connector */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center lg:hidden my-4">
                    <div className="bg-gradient-to-b from-gray-300 to-gray-400 w-0.5 h-6 relative">
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-full"></div>
                    </div>
                  </div>
                )}

                {/* Desktop Arrow Connector */}
                {index < steps.length - 1 && (index + 1) % 4 !== 0 && (
                  <div className="hidden lg:block absolute top-16 -right-4 z-0">
                    <ArrowRight className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12 sm:mb-16"
        >
          <div className="bg-gradient-to-br from-[#7CB342] via-[#1976D2] to-[#7CB342] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-6 sm:mb-8 text-center">
              This Flow Ensures
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white flex-shrink-0 mt-0.5" />
                  <p className="text-white text-sm sm:text-base font-medium leading-relaxed">
                    {benefit.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Process Flow Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-4 bg-gradient-to-r from-gray-100 to-gray-50 rounded-full px-4 sm:px-8 py-3 sm:py-4 shadow-lg border border-gray-200">
            {[
              "Enroll",
              "Distribute",
              "Collect",
              "Pay",
              "Warehouse",
              "Biofuel",
            ].map((step, index) => (
              <div key={index} className="flex items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                  {step}
                </span>
                {index < 5 && (
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[#7CB342] flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}